import React from 'react';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { parseBlocks } from 'services/parseUtils';
import { Freeform } from 'components/common/Freeform';
import { FreeformHeader } from 'components/common/Freeform/FreeformHeader';
import { FreeformImage } from 'components/common/Freeform/FreeformImage';
import { KitButton } from 'components/common/cms-kit';
import Section from 'components/common/subcomponents/Section';
import {
  headingColor,
  headingStyle,
} from 'services/styleUtils/headingStyleUtils';
import { getContentStyle } from 'services/styleUtils/contentStyleUtils';
import { buttonColor } from 'services/styleUtils/buttonStyleUtils';

const Container = styled('div')(({ $textPosition }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  '@media screen and (max-width: 1000px)': {
    width: '100%',
    flexDirection: 'column',
  },
  '@media screen and (min-width: 1001px)': {
    width: '100%',
    flexDirection: $textPosition === 'left' ? 'row-reverse' : 'row',
  },
}));

const ImageContainer = styled('div')({
  '@media screen and (max-width: 1000px)': {
    width: '100%',
  },
  '@media screen and (min-width: 1001px)': {
    width: '50%',
  },
});

const TextContainer = styled('div')(({ $textPosition }) => ({
  '@media screen and (max-width: 1000px)': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    marginTop: '20px',
  },
  '@media screen and (min-width: 1001px)': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    margin: $textPosition === 'left' ? '0 60px 0 40px' : '0 50px 0 70px',
    width: '45%',
  },
}));

const ButtonContainer = styled('div')({
  display: 'flex',
  flexFlow: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '10px 5px',
  width: '100%',
});

const HeadingTextAndMedia = (props) => {
  const {
    module: { fields },
  } = props;

  const callAction = async (url, target) => {
    const win = window.open(url, target || '_self');
    win.focus();
  };

  const theme = useTheme();

  const blocks = parseBlocks(fields.contentText);
  const contentStyle = getContentStyle(theme, fields.contentStyle?.fields);

  return (
    <Section styleProps={fields.sectionStyle} sectionId={fields.sectionId}>
      <Container $textPosition={fields.textPosition}>
        <ImageContainer>
          {fields?.image && (
            <FreeformImage
              file={{
                url: fields?.image.url,
                size: {
                  height: fields?.image.height,
                  width: fields?.image.width,
                },
              }}
              caption={fields?.image.label}
            />
          )}
        </ImageContainer>
        <TextContainer $textPosition={fields.textPosition}>
          <FreeformHeader
            level={fields?.headingLevel || 'h2'}
            text={fields?.headingText}
            color={headingColor(theme, fields.headingStyle?.fields?.color)}
            style={{
              marginBottom: '1rem',
              ...headingStyle(fields.headingStyle?.fields?.alignment),
            }}
          />

          <div
            style={{
              color: contentStyle.textColor,
              textAlign: contentStyle.alignment,
              fontSize: contentStyle.fontSize,
              lineHeight: contentStyle.lineHeight,
            }}
          >
            <Freeform blocks={blocks}></Freeform>
          </div>

          <ButtonContainer>
            {fields?.callsToAction
              ?.sort()
              .map(({ contentID, fields: { label, style, link } }) => (
                <KitButton
                  key={contentID}
                  round
                  size="sm"
                  style={{
                    backgroundColor: buttonColor(theme, style?.fields?.color),
                    marginRight: '1rem',
                  }}
                  onClick={(_e) => callAction(link.href, link.target)}
                >
                  {label}
                </KitButton>
              ))}
          </ButtonContainer>
        </TextContainer>
      </Container>
    </Section>
  );
};

export default HeadingTextAndMedia;
